/**
 * WarAirModule — the PERSISTENT piloted air war (2026-08-29 war rework):
 *
 *   Every hull is a `war_air_unit` ROW — a named versona pilot drafted for the
 *   WAR, not spawned per battle. Ships persist between ground battles and only
 *   fly home when the war resolves (visibly departing, never popping out).
 *
 *   SERVER owns existence / hp / state / anchors (warAirSweep); POSITIONS are
 *   client-choreographed pure functions of (row state, seed, wall clock) —
 *   lib/air_choreo.ts — so every client sees the same ballet with zero
 *   networking. States:
 *     patrol  fighters weave the band over the war front; the cruiser orbits
 *     escort  formate on the real marching craft (VersonaTravelModule pose)
 *             or a covering racetrack when the march isn't streaming
 *     engage  epoch-based dogfights: each cross-side pair holds a CHASER
 *             (tail-pursuit, delayed-path) and an EVADER (band + strafing
 *             runs on the enemy ground line) that swap every 90 s
 *     down    the cargo-pattern crash replay to the SERVER's crash point —
 *             k² fall, impact explosion, a listing wreck smoking until the
 *             row's TTL reaps it. The pilot's fate is the server's story
 *             (grave cause 'war', or a chute in the wind).
 *     home    a climbing beeline away from the front once peace lands.
 *
 *   THE PLAYER is a combatant: hulls are shootable (air_targets registry →
 *   AtlasVehicleModule's bolt loop); local bolt damage routes to the
 *   `shootWarAirUnit` reducer — the SERVER decides the kill (state 'down'),
 *   never the client. Return fire keeps the hostility latch (a side shoots
 *   back only after the player fires on it). Pilot NAME plates (≤600 m)
 *   put a face on every hull.
 *
 * Doctrine: hulls are master-cloned + markShared (release-closure law); FX
 * ride one CombatVfx; the fx root re-attaches across scene swaps (ensureVfx
 * law); nothing allocates per frame; pilots aloft join env.airHiddenIds so
 * the ground crowd never double-draws them.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../../state.js';
import { settlementSites } from '@shared/settlements.js';
import { resolvedCityName } from '../../lib/city_names.js';
import { bus } from '../../bus.js';
import { makeSystem, Phase, Sub } from '../../engine-compat.js';
import { prefabAssetId } from '../../lib/player_models.js';
import { markShared } from '../../lib/defer_dispose.js';
import { groundYIn } from '../../lib/grounding.js';
import { sfx, sfxAt, sfxLoop } from '../../lib/sfx.js';
import { reportThreat } from '../../lib/audio/threat.js';
import {
  cruiserPose, fighterPose, escortPose, homePose, departPose, strafeAim, airH,
  dogfightChaser, pursuitDelay, hullBank, DEPART_HIDE_ALT_M, type AirPose,
} from '../../lib/air_choreo.js';
import {
  upsertAirTarget, removeAirTarget, clearAirTargetsByPrefix, airTargetById, sideHostile,
} from '../../lib/air_targets.js';
import { CombatVfx } from './CombatVfx.js';
import { makePlate, type Plate } from './HealthBars.js';
import { nameFromTables } from '../../lib/versona_names.js';
import type { AssetModule } from '../AssetModule.js';
import type { EnvironmentModule } from '../EnvironmentModule.js';
import type { VersonaTravelModule } from '../VersonaTravelModule.js';

const RENDER_RANGE_M = 3400;     // render units whose anchor is this close
/** A dogfight this close overhead is COMBAT to the music (lib/audio/threat.ts) — under the strafing runs, not merely in sight of them. */
const AIRWAR_THREAT_M = 450;
const PLATE_RANGE_M = 600;
const CRUISER_LEN_M = 64;
const FIGHTER_LEN_M = 9;
const FIGHTER_HP_MAX = 36;       // mirrors server war_air_ops constants
const CRUISER_HP_MAX = 380;
const CRASH_ANIM_S = 4.5;        // the CargoSky crash-replay cadence
const SHOOT_CD_S = 0.35;         // min interval between shootWarAirUnit calls

interface WarAirRow {
  airId: bigint; warId: bigint; worldId: bigint; side: string; versonaId: bigint;
  kind: string; chassisKey: string; hp: number; state: string;
  anchorX: number; anchorZ: number; escortRef: bigint;
  crashX: number; crashZ: number;
  downedBy: { toHexString(): string };
  updatedAt: { microsSinceUnixEpoch: bigint };
}

/** A 'strafe' / 'missile' battle_event row (the server's own air blows — war tweaks 2026-09-17). */
interface AirEventRow { eventId: bigint; battleId: bigint; worldId: bigint; kind: string; actorId: bigint; targetId: bigint; x: number; z: number; tx: number; tz: number; dmg: number; detail: string }

interface UnitBody {
  obj: THREE.Object3D | null;
  chassisKey: string;
  versonaId: bigint;
  prevFx: number; prevFz: number;  // last frame's forward (the turn rate → the bank)
  bank: number;
  plate: Plate | null;
  lastState: string;
  lastPushedHp: number;          // what we last wrote into the air target
  shootCd: number;
  fireCd: number;                // return-fire cooldown
  prevRun: number;               // strafe ground-FX latch
  prevK: number;
  crashFrom: { x: number; y: number; z: number } | null;
  crashDone: boolean;
  downToasted: boolean;
  lastX: number; lastY: number; lastZ: number;
}

/** Box-fit a hull so its longest axis spans `len` meters. */
function fitLongest(obj: THREE.Object3D, len: number): void {
  const box = new THREE.Box3().setFromObject(obj);
  const s = new THREE.Vector3(); box.getSize(s);
  obj.scale.setScalar(len / Math.max(s.x, s.y, s.z, 0.001));
}

function tsSec(row: WarAirRow): number { return Number(row.updatedAt.microsSinceUnixEpoch) / 1e6; }

export class WarAirModule implements IModule {
  readonly name = 'WarAir';
  private app: App | null = null;
  private assets: AssetModule;
  private env: EnvironmentModule;
  private travel: VersonaTravelModule | null = null;
  private vfx: CombatVfx | null = null;
  private subbed = false;
  private masters = new Map<string, THREE.Object3D>();   // chassisKey → hull
  private mastersTried = new Set<string>();
  private units = new Map<string, UnitBody>();            // airId → body
  /** Pilot names by versona (2026-09-12): nameFromTables spreads FOUR tables (npc_agent ≈ 6,000 rows) — it ran per unit per FRAME. A first-name-only answer (household tables not yet synced) retries every 5 s. */
  private names = new Map<bigint, { name: string; full: boolean; at: number }>();
  /** Terrain under each unit's (constant) anchor — 4 analytic stampedHeight evaluations per sample outside the chunk ring. */
  private anchorGround = new Map<string, number>();
  private hornedWars = new Set<string>();
  private scanAcc = 0;
  private duelAcc = 0;
  private loop: ReturnType<typeof sfxLoop> | null = null;
  private readonly _v1 = new THREE.Vector3();
  private readonly _v2 = new THREE.Vector3();
  private readonly _bx = new THREE.Vector3();
  private readonly _by = new THREE.Vector3();
  private readonly _bz = new THREE.Vector3();
  private readonly _m = new THREE.Matrix4();
  private strafeFx = 0;
  private strafeHitFx = 0;
  private missileFx = 0;
  private dogfightFx = 0;
  private dogfightHitFx = 0;
  /** The wing's home town per (war, side): a bearing from the anchor and a name (war tweaks 2026-09-18 — a departure flies HOME). */
  private homes = new Map<string, { bearing: number; name: string; x: number; z: number } | null>();
  private pendingFx: AirEventRow[] = [];

  constructor(assets: AssetModule, env: EnvironmentModule) { this.assets = assets; this.env = env; }

  /** Late wiring: VersonaTravelModule registers after us (escort anchors). */
  attachTravel(travel: VersonaTravelModule): void { this.travel = travel; }

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('WarAir', Phase.Animation, 42, (dt) => this.frame(dt), Sub.Update));
    (globalThis as Record<string, unknown>).__warAir = () => {
      const rows = this.rows();
      const byState: Record<string, number> = {};
      for (const r of rows) byState[r.state] = (byState[r.state] ?? 0) + 1;
      return {
        rows: rows.length,
        rendered: [...this.units.values()].filter((u) => u.obj?.visible).length,
        byState,
        strafeFx: this.strafeFx,
        strafeHitFx: this.strafeHitFx,
        missileFx: this.missileFx,
        dogfightFx: this.dogfightFx,
        dogfightHitFx: this.dogfightHitFx,
        homes: [...this.homes.entries()].map(([k, h]) => ({ key: k, name: h?.name ?? null, bearing: h ? +h.bearing.toFixed(2) : null })),
        pilots: rows.map((r) => ({ airId: r.airId.toString(), side: r.side, kind: r.kind, hp: r.hp, state: r.state, name: this.pilotName(r), bankDeg: Math.round((this.units.get(r.airId.toString())?.bank ?? 0) * 57.3) })),
      };
    };
  }

  /** All war_air_unit rows in THIS world (the whole-war fleet, every state). */
  private rows(): WarAirRow[] {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return [];
    const out: WarAirRow[] = [];
    try {
      for (const r of (conn.db as unknown as { warAirUnit: { iter(): Iterable<WarAirRow> } }).warAirUnit.iter()) {
        if (r.worldId === atlas.currentWorldId) out.push(r);
      }
    } catch { /* pre-publish server / table mid-churn */ }
    return out;
  }

  private ensureSub(): void {
    if (this.subbed || !atlas.conn) return;
    this.subbed = true;
    try {
      atlas.conn.subscriptionBuilder().subscribe(['SELECT * FROM war_air_unit', 'SELECT * FROM war_state']); // war_state: the wing's HOME town (a handful of rows)
      // The server's own air blows: a strafe fired at a unit it saw, a cruiser's missile — drawn from the INSERT.
      (atlas.conn.db as unknown as { battleEvent: { onInsert(cb: (c: unknown, row: AirEventRow) => void): void } }).battleEvent.onInsert((_c, row) => {
        if ((row.kind !== 'strafe' && row.kind !== 'missile' && row.kind !== 'dogfight') || row.worldId !== atlas.currentWorldId) return;
        if (Math.hypot(row.tx - atlas.localX, row.tz - atlas.localZ) > RENDER_RANGE_M) return;
        this.pendingFx.push(row);
      });
    } catch { this.subbed = false; }
  }

  /** Stream a chassis hull master once per key (registry may lag). */
  private ensureMaster(chassisKey: string): THREE.Object3D | null {
    const hit = this.masters.get(chassisKey);
    if (hit) return hit;
    if (this.mastersTried.has(chassisKey) || !atlas.conn) return null;
    const conn = atlas.conn;
    const fallback = chassisKey.startsWith('war_capital') ? 'prop:ship_sovereign' : 'prop:ship_fighter';
    const id = prefabAssetId(conn, `prop:${chassisKey}`) ?? prefabAssetId(conn, fallback);
    if (id === null) return null; // prefab registry not replicated yet — retry next scan
    this.mastersTried.add(chassisKey);
    void this.assets.load(id).then((master) => {
      markShared(master);
      this.masters.set(chassisKey, master);
    }).catch(() => { /* hull unavailable — that slot flies unrendered */ });
    return null;
  }

  private ensureVfx(): CombatVfx | null {
    const scene = this.app?.world.scene;
    if (!scene) return null;
    if (!this.vfx) this.vfx = new CombatVfx();
    if (this.vfx.root.parent !== scene) scene.add(this.vfx.root);
    return this.vfx;
  }

  private pilotName(r: WarAirRow): string {
    const now = performance.now();
    const hit = this.names.get(r.versonaId);
    if (hit && (hit.full || now - hit.at < 5000)) return hit.name;
    let name = r.kind === 'cruiser' ? 'Cruiser captain' : 'Pilot';
    let full = false;
    try {
      const n = nameFromTables(atlas.conn, r.versonaId);
      if (n) { name = n; full = n.includes(' '); }
    } catch { /* tables mid-churn */ }
    this.names.set(r.versonaId, { name, full, at: now });
    return name;
  }

  private body(r: WarAirRow): UnitBody {
    return {
      obj: null, chassisKey: r.chassisKey, versonaId: r.versonaId, prevFx: 0, prevFz: 0, bank: 0,
      plate: null, lastState: '', lastPushedHp: -1,
      shootCd: 0, fireCd: 2, prevRun: -1, prevK: 0,
      crashFrom: null, crashDone: false, downToasted: false,
      lastX: 0, lastY: 0, lastZ: 0,
    };
  }

  private hull(b: UnitBody, len: number): THREE.Object3D | null {
    if (b.obj) return b.obj;
    const master = this.ensureMaster(b.chassisKey);
    if (!master) return null;
    const scene = this.app?.world.scene;
    if (!scene) return null;
    const clone = master.clone();
    fitLongest(clone, len);
    scene.add(clone);
    b.obj = clone;
    return clone;
  }

  private dropUnit(key: string, b: UnitBody): void {
    if (b.obj) { this.app?.world.scene?.remove(b.obj); b.obj = null; }
    if (b.plate) { this.app?.world.scene?.remove(b.plate.sprite); b.plate.dispose(); b.plate = null; }
    removeAirTarget(`war:u:${key}`);
    this.units.delete(key);
    this.anchorGround.delete(key);
  }

  /** Pose a fighter by row state (battle-relative to its anchor). */
  private fighterStatePose(r: WarAirRow, seed: string, i: number, t: number, sameSide: WarAirRow[], enemies: WarAirRow[]): AirPose {
    const side = r.side === 'a' ? 'a' : 'b';
    if (r.state === 'escort') {
      // Real march when it streams; covering racetrack otherwise.
      const craft = r.escortRef !== 0n ? this.travel?.craftPose(r.escortRef) ?? null : null;
      if (craft) {
        const h = airH(`${seed}|form${r.airId}`);
        const th = h * Math.PI * 2 + t * 0.5;
        return {
          x: craft.x - r.anchorX + Math.cos(th) * (26 + i * 10),
          z: craft.z - r.anchorZ + Math.sin(th) * (26 + i * 10),
          y: 34 + i * 7 + Math.sin(t * 0.4 + h * 6) * 4, // above the marching craft
          strafe: 0, runIdx: 0,
        };
      }
      return escortPose(seed, side, i, t);
    }
    if (r.state === 'engage' && enemies.length > 0) {
      const rank = sameSide.findIndex((s) => s.airId === r.airId);
      const pair = Math.min(rank < 0 ? 0 : rank, enemies.length - 1);
      if (dogfightChaser(seed, side, pair, t)) {
        // Tail chase: fly the enemy's own path, delayed — pure and smooth.
        const foe = enemies[pair % enemies.length];
        const foeSide = foe.side === 'a' ? 'a' : 'b';
        const p = fighterPose(seed, foeSide, Number(foe.airId % 7n), t - pursuitDelay(t)); // lag pursuit, lead pursuit by turns
        return { x: p.x, z: p.z, y: p.y + 4, strafe: 0, runIdx: 0 };
      }
      return fighterPose(seed, side, i, t); // evader: the band + strafing runs
    }
    return fighterPose(seed, side, i, t);   // patrol
  }

  private frame(dt: number): void {
    const conn = atlas.conn;
    this.scanAcc += dt;
    const scan = this.scanAcc >= 1;
    if (scan) { this.scanAcc = 0; this.ensureSub(); }
    if (!conn || atlas.currentWorldId === null || atlas.spaceFlight) {
      if (scan && this.units.size > 0) { for (const [k, b] of [...this.units]) this.dropUnit(k, b); this.env.airHiddenIds.clear(); }
      if (this.loop) this.loop.set(0, 1, 0.6);
      return;
    }
    const rows = this.rows();
    // Reap bodies whose rows vanished (peace TTL / world change / reset).
    if (scan) {
      const live = new Set(rows.map((r) => r.airId.toString()));
      for (const [k, b] of [...this.units]) if (!live.has(k)) this.dropUnit(k, b);
      this.env.airHiddenIds.clear();
      for (const r of rows) if (r.state !== 'down') this.env.airHiddenIds.add(r.versonaId.toString());
    }
    if (rows.length === 0) { if (this.loop) this.loop.set(0, 1, 0.6); return; }

    const t = Date.now() / 1000; // the shared wall clock — every client sees the same ballet
    const wid = atlas.currentWorldId ?? atlas.commonsWorldId;
    const vfx = this.ensureVfx();
    let nearest = Infinity;
    let nearX = 0, nearZ = 0; // where the nearest front IS — the din is heard from there (2026-09-20)
    let engagedNear = false; // a wing in a dogfight within AIRWAR_THREAT_M of you: combat, to the score

    // Group per war for pairing + the horn.
    const byWar = new Map<string, WarAirRow[]>();
    for (const r of rows) {
      const k = r.warId.toString();
      let g = byWar.get(k);
      if (!g) { g = []; byWar.set(k, g); }
      g.push(r);
    }

    for (const [warKey, group] of byWar) {
      const seed = `war${warKey}`;
      group.sort((x, y) => (x.airId < y.airId ? -1 : 1)); // stable pairing order
      const flying = (r: WarAirRow): boolean => r.state !== 'down' && r.state !== 'home' && r.state !== 'repair' && r.state !== 'depart';
      const fightersA = group.filter((r) => r.kind === 'fighter' && r.side === 'a' && flying(r));
      const fightersB = group.filter((r) => r.kind === 'fighter' && r.side === 'b' && flying(r));
      const cruisersA = group.filter((r) => r.kind === 'cruiser' && r.side === 'a' && flying(r));
      const cruisersB = group.filter((r) => r.kind === 'cruiser' && r.side === 'b' && flying(r));

      for (const r of group) {
        const key = r.airId.toString();
        const anchorD = Math.hypot(r.anchorX - atlas.localX, r.anchorZ - atlas.localZ);
        let b = this.units.get(key);
        if (anchorD > RENDER_RANGE_M) { if (b?.obj) b.obj.visible = false; if (b?.plate) b.plate.sprite.visible = false; continue; }
        if (anchorD < nearest) { nearest = anchorD; nearX = r.anchorX; nearZ = r.anchorZ; }
        if (r.state === 'engage' && anchorD < AIRWAR_THREAT_M) engagedNear = true;
        if (!b) { b = this.body(r); this.units.set(key, b); }
        if (!this.hornedWars.has(warKey) && r.state !== 'down') {
          this.hornedWars.add(warKey);
          bus.emit('atlas:status', { text: '⚔ War wings overhead — pilots hold the sky until this war ends' });
          sfx('sfx_war_horn', { gain: 0.4, cooldownMs: 4000 });
        }

        const side = r.side === 'a' ? 'a' : 'b';
        const cruiser = r.kind === 'cruiser';
        const i = Number(r.airId % 7n);
        let gy = this.anchorGround.get(key);
        if (gy === undefined) { gy = groundYIn(conn, wid, r.anchorX, r.anchorZ); this.anchorGround.set(key, gy); }
        const targetId = `war:u:${key}`;

        // ── DOWN: the crash replay to the server's crash point, then the wreck ──
        if (r.state === 'down') {
          removeAirTarget(targetId);
          this.env.airHiddenIds.delete(r.versonaId.toString());
          const crashT0 = tsSec(r);
          const k01 = Math.min(1, Math.max(0, (t - crashT0) / CRASH_ANIM_S));
          const cgy = groundYIn(conn, wid, r.crashX, r.crashZ);
          if (!b.downToasted) {
            b.downToasted = true;
            const nm = this.pilotName(r);
            if (conn.identity && r.downedBy.toHexString() === conn.identity.toHexString()) {
              bus.emit('atlas:status', { text: `☠ You shot down ${nm} — their people will remember who pulled the trigger` });
            } else if (anchorD < 2200) {
              bus.emit('atlas:status', { text: `✈ ${nm}'s ${cruiser ? 'cruiser' : 'fighter'} is going down over the front` });
            }
          }
          const obj = this.hull(b, cruiser ? CRUISER_LEN_M : FIGHTER_LEN_M);
          if (!b.crashFrom) {
            // Where the fall starts: the last rendered spot, else the band pose at the down-stamp.
            if (b.obj && b.lastY > 0) b.crashFrom = { x: b.lastX, y: b.lastY, z: b.lastZ };
            else {
              const p = cruiser ? cruiserPose(seed, side, crashT0) : fighterPose(seed, side, i, crashT0);
              b.crashFrom = { x: r.anchorX + p.x, y: gy + p.y, z: r.anchorZ + p.z };
            }
          }
          if (obj && !b.crashDone) {
            obj.visible = true;
            const kk = k01 * k01; // accelerating fall
            obj.position.set(
              b.crashFrom.x + (r.crashX - b.crashFrom.x) * k01,
              b.crashFrom.y + (cgy + 1.2 - b.crashFrom.y) * kk,
              b.crashFrom.z + (r.crashZ - b.crashFrom.z) * k01,
            );
            obj.rotation.z = k01 * 0.55;
            obj.rotation.x = k01 * 0.2;
            if (vfx && Math.floor(t * 3) % 2 === 0) vfx.impact(this._v1.copy(obj.position), 0.6, 0x666666); // smoke trail down
            if (k01 >= 1) {
              b.crashDone = true;
              if (vfx) vfx.explosion(this._v1.set(r.crashX, cgy + 2, r.crashZ), cruiser ? 6 : 3, 0xffa060);
              sfxAt('sfx_ship_crash', { x: r.crashX, y: cgy + 2, z: r.crashZ }, { gain: 0.7, cooldownMs: 800, caption: 'A ship crashes' });
            }
          } else if (k01 >= 1) b.crashDone = true;
          if (obj && b.crashDone) {
            // The wreck: listing where it fell, smoke curling off until the TTL reaps the row.
            obj.visible = true;
            obj.position.set(r.crashX, cgy + 1.0, r.crashZ);
            obj.rotation.set(0.2, airH(`${seed}|wr${key}`) * Math.PI * 2, 0.55);
            if (vfx && Math.floor(t * 2) % 5 === 0) vfx.impact(this._v1.set(r.crashX, cgy + 4, r.crashZ), 0.7, 0x555555);
          }
          if (b.plate && obj) {
            b.plate.sprite.visible = anchorD < PLATE_RANGE_M * 2;
            b.plate.sprite.position.set(obj.position.x, obj.position.y + (cruiser ? 26 : 6), obj.position.z);
            b.plate.draw({ name: this.pilotName(r), sub: 'shot down', hp: 0, morale: 0, side, state: 'fallen' });
          }
          b.lastState = r.state;
          continue;
        }

        // ── REPAIR (campaign): set down at the outpost's bay while the mechanics work ──
        if (r.state === 'repair') {
          removeAirTarget(targetId); // a hull on the ground is the ground fight's business
          const obj = this.hull(b, cruiser ? CRUISER_LEN_M : FIGHTER_LEN_M);
          if (obj) {
            obj.visible = true;
            obj.position.set(r.anchorX + (cruiser ? 0 : 6), gy + (cruiser ? 3.5 : 1.1), r.anchorZ - (cruiser ? 26 : 8));
            obj.rotation.set(0, airH(`${seed}|bay${key}`) * Math.PI * 2, 0);
            b.lastX = obj.position.x; b.lastY = obj.position.y; b.lastZ = obj.position.z;
            if (vfx && Math.floor(t * 2) % 3 === 0) vfx.impact(this._v1.set(obj.position.x, obj.position.y + 1, obj.position.z), 0.3, 0xffd27a); // the welders' sparks
            if (b.plate) {
              b.plate.sprite.visible = anchorD < PLATE_RANGE_M;
              b.plate.sprite.position.set(obj.position.x, obj.position.y + (cruiser ? 26 : 6), obj.position.z);
              b.plate.draw({ name: this.pilotName(r), sub: 'in the bay — under repair', hp: r.hp, morale: 100, side, state: 'repair' });
            }
          }
          b.lastState = r.state;
          continue;
        }

        // ── FLYING states: pose from the row ──
        let wx: number, wy: number, wz: number;
        let aheadX: number, aheadY: number, aheadZ: number;
        if (r.state === 'depart' || r.state === 'home') {
          // THE WING FLIES HOME (2026-09-20, the ARRIVAL LAW): a departure flies the WHOLE way to
          // the wing's own town — climb-out, cruise, a 3° approach, touchdown — and stays drawn the
          // entire journey. It is never hidden for crossing a radius: it leaves your sight by flying
          // over the horizon, like anything else. Only a hull with NO resolvable town falls back to
          // the legacy climb-out, and only that one is hidden (it is climbing away to space).
          const since = Math.max(0, t - tsSec(r));
          const home = this.homeOf(conn, r, wid);
          const posef = r.state === 'depart' ? departPose : homePose;
          const distM = home ? Math.hypot(home.x - r.anchorX, home.z - r.anchorZ) : undefined;
          const p = posef(seed, key, since, cruiser, home?.bearing, distM);
          const p2 = posef(seed, key, since + 1, cruiser, home?.bearing, distM);
          wx = r.anchorX + p.x; wy = gy + p.y; wz = r.anchorZ + p.z;
          aheadX = r.anchorX + p2.x; aheadY = gy + p2.y; aheadZ = r.anchorZ + p2.z;
          removeAirTarget(targetId); // no longer shootable
          if (!home && p.y > DEPART_HIDE_ALT_M) {
            if (b.obj) b.obj.visible = false;
            if (b.plate) b.plate.sprite.visible = false;
            b.lastState = r.state;
            continue;
          }
        } else if (cruiser) {
          // Several cruisers a side (the economy sizes the wing): each takes its own station by rank among its side's live cruisers.
          const cIdx = Math.max(0, (side === 'a' ? cruisersA : cruisersB).findIndex((c) => c.airId === r.airId));
          const p = cruiserPose(seed, side, t, cIdx);
          const p2 = cruiserPose(seed, side, t + 1.2, cIdx);
          wx = r.anchorX + p.x; wy = gy + p.y; wz = r.anchorZ + p.z;
          aheadX = r.anchorX + p2.x; aheadY = wy; aheadZ = r.anchorZ + p2.z;
        } else {
          const same = side === 'a' ? fightersA : fightersB;
          const foes = side === 'a' ? fightersB : fightersA;
          const p = this.fighterStatePose(r, seed, i, t, same, foes);
          const p2 = this.fighterStatePose(r, seed, i, t + 0.35, same, foes);
          wx = r.anchorX + p.x; wy = gy + p.y; wz = r.anchorZ + p.z;
          aheadX = r.anchorX + p2.x; aheadY = gy + p2.y; aheadZ = r.anchorZ + p2.z;
          // Strafing ground FX: the EVADER's dives while the war is engaged here.
          if (r.state === 'engage' && p.strafe > 0) {
            if (b.prevK < 0.5 && p.strafe >= 0.5 && b.prevRun !== p.runIdx) {
              b.prevRun = p.runIdx;
              const aim = strafeAim(seed, side, i, p.runIdx);
              const ax = r.anchorX + aim.x, az = r.anchorZ + aim.z;
              const ay = groundYIn(conn, wid, ax, az);
              if (vfx) {
                vfx.bolt(this._v1.set(wx, wy, wz), this._v2.set(ax, ay + 0.5, az), 0xffe6a8);
                vfx.explosion(this._v2.set(ax, ay + 0.6, az), 1.6, 0xffb070);
              }
              const d = Math.hypot(ax - atlas.localX, az - atlas.localZ);
              sfxAt('sfx_blaster_fire', { x: wx, y: wy, z: wz }, { gain: Math.max(0.08, 0.4 - d * 0.0004), cooldownMs: 180 }); // from the diving hull…
              sfxAt('sfx_battle_explosion', { x: ax, y: ay + 0.6, z: az }, { gain: Math.max(0.08, 0.45 - d * 0.0004), cooldownMs: 220, caption: 'Explosion' }); // …to where it lands
              this.strafeFx++;
            }
          }
          b.prevK = p.strafe;
        }

        const obj = this.hull(b, cruiser ? CRUISER_LEN_M : FIGHTER_LEN_M);
        if (obj) {
          obj.visible = true;
          obj.position.set(wx, wy, wz);
          this._v1.set(aheadX - wx, aheadY - wy, aheadZ - wz);
          if (this._v1.lengthSq() > 0.01) {
            // A HULL FLIES NOSE-FIRST IN ABSOLUTE SPACE (war tweaks 2026-09-17): an explicit basis (nose −X → forward),
            // never lookAt — lookAt reads the render-space matrixWorld (the scene sits at −origin under the FP64 rebase)
            // against an absolute target, so away from the origin the hull turned the wrong way (the sideways cruiser).
            // The bank is a coordinated turn's: atan(v·ω/g) from the turn rate, so a hull rolls INTO its turns.
            const dtAhead = r.state === 'home' || r.state === 'depart' ? 1 : cruiser ? 1.2 : 0.35;
            const speed = this._v1.length() / dtAhead;
            this._v1.normalize();
            let omega = 0;
            if (b.prevFx !== 0 || b.prevFz !== 0) {
              const cross = b.prevFz * this._v1.x - b.prevFx * this._v1.z; // (prev × cur)·up — positive = a left turn
              const dot = b.prevFx * this._v1.x + b.prevFz * this._v1.z;
              omega = Math.atan2(cross, dot) / Math.max(dt, 1e-3);
            }
            b.prevFx = this._v1.x; b.prevFz = this._v1.z;
            b.bank += (hullBank(speed, omega) - b.bank) * Math.min(1, dt * 2.5);
            this.orient(obj, this._v1, b.bank);
          }
        }
        b.lastX = wx; b.lastY = wy; b.lastZ = wz;

        // ── The shootable target: server hp mirrored DOWN, bolt hits routed UP ──
        if (r.state !== 'home' && r.state !== 'depart') {
          upsertAirTarget({
            id: targetId, battleId: r.warId, side, kind: cruiser ? 'cruiser' : 'fighter',
            x: wx, y: wy, z: wz, r: cruiser ? CRUISER_LEN_M * 0.45 : FIGHTER_LEN_M * 0.75,
            hp: r.hp, hitByPlayer: false, obj: b.obj,
          });
          const tgt = airTargetById(targetId);
          b.shootCd -= dt;
          if (tgt) {
            if (b.lastPushedHp >= 0 && tgt.hp < b.lastPushedHp && b.shootCd <= 0) {
              // The player's bolts landed since we last synced — tell the server
              // (it applies the damage; the KILL is its call via state 'down').
              b.shootCd = SHOOT_CD_S;
              (conn.reducers as unknown as { shootWarAirUnit(a: { airId: bigint }): Promise<void> })
                .shootWarAirUnit({ airId: r.airId }).catch(() => { /* out of range / already down */ });
              sfx('sfx_kill_confirm', { gain: 0.2, cooldownMs: 900 });
            }
            tgt.hp = r.hp; // the server is the health authority
            b.lastPushedHp = r.hp;
          }
        }

        // ── Return fire (the hostility latch — only once fired upon) ──
        if (r.state !== 'home' && r.state !== 'depart' && atlas.skyFlight && sideHostile(r.warId, side)) {
          b.fireCd -= dt;
          const pd = Math.hypot(wx - atlas.localX, wy - atlas.localY, wz - atlas.localZ);
          const range = cruiser ? 1400 : 950;
          if (b.fireCd <= 0 && pd < range) {
            b.fireCd = cruiser ? 1.8 : 2.2 + airH(`${seed}|fc${key}|${Math.floor(t)}`) * 1.2;
            vfx?.bolt(this._v1.set(wx, wy - (cruiser ? 8 : 0), wz), this._v2.set(atlas.localX, atlas.localY, atlas.localZ), cruiser ? 0xffa080 : 0xff9090);
            sfxAt(cruiser ? 'sfx_capital_gun' : 'sfx_enemy_cannon', { x: wx, y: wy, z: wz }, { gain: Math.max(0.1, 0.45 - pd * 0.0003), cooldownMs: 280, caption: 'Incoming fire' }); // WHERE the gun is — it is shooting at you
            if (airH(`${seed}|fh${key}|${Math.floor(t * 5)}`) < (cruiser ? 0.3 : 0.38)) {
              bus.emit('atlas:ship-damage', { amount: cruiser ? 9 : 5, why: cruiser ? 'cruiser flak' : 'fighter fire' });
            }
          }
        }

        // ── The pilot's name over the hull ──
        if (anchorD < PLATE_RANGE_M * 3 && obj) {
          if (!b.plate) { b.plate = makePlate(); this.app?.world.scene?.add(b.plate.sprite); }
          const pd = Math.hypot(wx - atlas.localX, wz - atlas.localZ);
          b.plate.sprite.visible = pd < PLATE_RANGE_M;
          if (b.plate.sprite.visible) {
            b.plate.sprite.position.set(wx, wy + (cruiser ? 40 : 8), wz);
            const sub = r.state === 'engage' ? 'dogfight!' : r.state === 'escort' ? 'escorting the march'
              : r.state === 'home' ? `flying home${this.homeOf(conn, r, wid)?.name ? ` to ${this.homeOf(conn, r, wid)!.name}` : ''}` : r.state === 'depart' ? `returning to ${this.homeOf(conn, r, wid)?.name ?? 'base'}` : cruiser ? 'cruiser captain — on station' : 'combat air patrol';
            const hpPct = (r.hp / (cruiser ? CRUISER_HP_MAX : FIGHTER_HP_MAX)) * 100;
            b.plate.draw({ name: this.pilotName(r), sub, hp: hpPct, morale: hpPct, side, state: r.state });
          }
        } else if (b.plate) b.plate.sprite.visible = false;
        b.lastState = r.state;
      }

      // ── The dogfight reads as a FIGHT: each pair's CHASER fires at its evader (war tweaks 2026-09-18) ──
      // The same pairing the poses use (rank by airId, side a chases on even epochs — dogfightChaser); the bursts
      // that WOUND come from the server's 'dogfight' events; these are the guns talking between them.
      this.duelAcc += dt;
      if (this.duelAcc >= 1.4 && vfx && fightersA.length > 0 && fightersB.length > 0) {
        this.duelAcc = 0;
        const engaged = [...fightersA, ...fightersB].some((r) => r.state === 'engage');
        const pairs = Math.min(fightersA.length, fightersB.length);
        for (let i = 0; i < pairs; i++) {
          if (!engaged && airH(`${seed}|pot|${i}|${Math.floor(t / 4)}`) >= 0.25) continue; // patrols trade potshots rarely
          const aChases = dogfightChaser(seed, 'a', i, t);
          const ba = this.units.get(fightersA[i].airId.toString()), bb = this.units.get(fightersB[i].airId.toString());
          const shooter = aChases ? ba : bb, victim = aChases ? bb : ba;
          if (!shooter?.obj?.visible || !victim?.obj?.visible) continue;
          if (shooter.obj.position.distanceTo(victim.obj.position) > 420) continue; // out of the guns' reach — no tracer
          vfx.bolt(this._v1.copy(shooter.obj.position), this._v2.copy(victim.obj.position).addScalar((airH(`${seed}|jit|${i}|${Math.floor(t)}`) - 0.5) * 6), aChases ? 0xffe6a8 : 0x9fe8ff);
          const pd = Math.hypot(shooter.lastX - atlas.localX, shooter.lastZ - atlas.localZ);
          if (pd < 1600) sfxAt('sfx_ship_cannon', { x: shooter.lastX, y: shooter.lastY, z: shooter.lastZ }, { gain: Math.max(0.05, 0.25 - pd * 0.0001), cooldownMs: 400, bus: 'combat', priority: 45 });
        }
      }
    }

    // The server's strafes and missiles (battle_event inserts), drawn once the scene has a vfx root.
    if (this.pendingFx.length > 0 && vfx) {
      const q = this.pendingFx; this.pendingFx = [];
      for (const e of q) this.airStrikeFx(e, vfx, wid);
    }

    // The war din from above (distance-faded loop off the nearest front).
    // ITS OWN loop of the clip (scope `warair`): the sector's fleet war used to share this one source — "the last set() wins".
    if (!this.loop) this.loop = sfxLoop('sfx_fleet_battle', 'warair');
    if (nearest !== Infinity) this.loop.at({ x: nearX, y: atlas.localY + 140, z: nearZ }); // from above, toward the front
    // Standing under a DOGFIGHT is combat to the score (a lease, renewed each tick — a wing that departs lets it lapse).
    if (engagedNear) reportThreat('airwar', performance.now());
    this.loop.set(nearest === Infinity ? 0 : Math.max(0, 0.5 - nearest * 0.00022), 1, 0.8);
    vfx?.update(dt);
  }

  /** The wing's HOME for a row: its side's settlement of the war (the pilots are drafted there) → the site centre,
   *  as a bearing from the row's anchor and a name; null while the war row or the sites are not replicated. Cached per (war, side, anchor). */
  private homeOf(conn: NonNullable<typeof atlas.conn>, r: WarAirRow, wid: bigint): { bearing: number; name: string; x: number; z: number } | null {
    const key = `${r.warId}|${r.side}|${Math.round(r.anchorX / 50)}|${Math.round(r.anchorZ / 50)}`;
    const hit = this.homes.get(key);
    if (hit !== undefined && (hit !== null || Math.floor(Date.now() / 5000) % 3 !== 0)) return hit; // a miss retries every ~15 s
    let out: { bearing: number; name: string; x: number; z: number } | null = null;
    try {
      const war = (conn.db as unknown as { warState?: { warId: { find(id: bigint): { aggressorSid: bigint; defenderSid: bigint } | undefined } } }).warState?.warId.find(r.warId);
      const world = (conn.db as unknown as { world: { worldId: { find(id: bigint): { terrainSeed: string } | undefined } } }).world.worldId.find(wid);
      if (war && world) {
        const sid = r.side === 'a' ? war.aggressorSid : war.defenderSid;
        const site = settlementSites(world.terrainSeed).find((s) => s.id === Number(sid) - 1); // DB settlementId = site.id + 1
        if (site) out = { bearing: Math.atan2(site.centerZ - r.anchorZ, site.centerX - r.anchorX), name: resolvedCityName(site, conn), x: site.centerX, z: site.centerZ };
      }
    } catch { out = null; }
    this.homes.set(key, out);
    return out;
  }

  /** Seat a hull's basis: local +X = −forward (the nose is −X), local +Y = up orthogonalised against the forward (pitch follows a climb), local +Z = X × Y; then the bank about the fuselage axis. */
  private orient(obj: THREE.Object3D, f: THREE.Vector3, bank: number): void {
    const X = this._bx.copy(f).negate();
    const Y = this._by.set(0, 1, 0);
    Y.addScaledVector(f, -Y.dot(f)).normalize();
    if (Y.lengthSq() < 1e-6) Y.set(0, 1, 0); // straight up or down — keep world up
    const Z = this._bz.crossVectors(X, Y);
    this._m.makeBasis(X, Y, Z);
    obj.quaternion.setFromRotationMatrix(this._m);
    obj.rotateX(bank);
  }

  /** A server 'strafe' / 'missile' / 'dogfight' event: the shot leaves the hull and lands where the server put it — on the unit it wounded, the enemy cruiser, or the evader's hull. */
  private airStrikeFx(e: AirEventRow, vfx: CombatVfx, wid: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    let from: UnitBody | null = null;
    for (const b of this.units.values()) if (b.versonaId === e.actorId && b.obj?.visible) { from = b; break; }
    if (e.kind === 'dogfight') {
      // THE DOGFIGHT IS REAL: the chaser's burst on its evader — a bolt hull to hull, a hit blooms and smokes on the victim.
      const victimId = e.detail.startsWith('air:') ? e.detail.slice(4).split('|')[0] : '';
      const victim = this.units.get(victimId) ?? null;
      const hit = e.dmg > 0;
      const fx = from ? from.lastX : e.x, fy = from ? from.lastY : groundYIn(conn, wid, e.x, e.z) + 140, fz = from ? from.lastZ : e.z;
      const tx = victim ? victim.lastX : e.tx, ty = victim ? victim.lastY : groundYIn(conn, wid, e.tx, e.tz) + 140, tz = victim ? victim.lastZ : e.tz;
      vfx.bolt(this._v1.set(fx, fy, fz), this._v2.set(tx + (hit ? 0 : 9), ty + (hit ? 0 : 5), tz), 0xffd27a);
      if (hit) { vfx.explosion(this._v2.set(tx, ty, tz), 2.2, 0xffa060); vfx.impact(this._v2.set(tx, ty - 1, tz), 0.8, 0x777777); }
      const d = Math.hypot(tx - atlas.localX, tz - atlas.localZ);
      sfxAt(hit ? 'sfx_hull_hit' : 'sfx_ship_cannon', { x: tx, y: ty, z: tz }, { gain: Math.max(0.06, 0.4 - d * 0.0004), cooldownMs: 120, bus: 'combat', priority: 45 });
      this.dogfightFx++; if (hit) this.dogfightHitFx++;
      return;
    }
    const gy = groundYIn(conn, wid, e.tx, e.tz);
    const missile = e.kind === 'missile';
    const airHit = missile && e.detail.startsWith('air:');
    const tgtBody = airHit ? this.units.get(e.detail.slice(4)) ?? null : null;
    const ty = airHit ? (tgtBody?.lastY ?? gy + 200) : gy + 0.8;
    const fx = from ? from.lastX : e.x, fy = from ? from.lastY : gy + (missile ? 220 : 70), fz = from ? from.lastZ : e.z;
    vfx.bolt(this._v1.set(fx, fy - (missile ? 6 : 0), fz), this._v2.set(e.tx, ty, e.tz), missile ? 0xffb070 : 0xffe6a8);
    const hit = e.dmg > 0;
    vfx.explosion(this._v2.set(e.tx, ty + (airHit ? 0 : 0.6), e.tz), missile ? (airHit ? 5 : 3.4) : hit ? 1.4 : 0.9, missile ? 0xff9a50 : 0xffb070);
    const d = Math.hypot(e.tx - atlas.localX, e.tz - atlas.localZ);
    sfxAt(missile ? 'sfx_battle_explosion' : 'sfx_blaster_fire', { x: e.tx, y: ty, z: e.tz }, { gain: Math.max(0.08, (missile ? 0.6 : 0.35) - d * 0.0004), cooldownMs: missile ? 300 : 150 });
    if (missile) this.missileFx++; else if (hit) this.strafeHitFx++;
  }

  dispose(): void {
    for (const [k, b] of [...this.units]) this.dropUnit(k, b);
    clearAirTargetsByPrefix('war:');
    this.env.airHiddenIds.clear();
    if (this.vfx) { this.vfx.dispose(); this.vfx = null; }
    if (this.loop) this.loop.set(0, 1, 0.3);
  }
}
